import { describeQueryShape, type QueryShape } from '@aibi/analysis-engine';
import type { AnalysisState, VisualizationType } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import type { AIContext, AIVisualizationCapability } from '../context/ai-context.js';

/** Sugerencia de visualizacion. Nunca se aplica sola: la UI pide confirmacion explicita. */
export interface VisualizationSuggestion {
  readonly type: VisualizationType;
  readonly label: string;
  /** Claves de dimension y medida del analisis que la visualizacion usaria. */
  readonly requiredDimensions: readonly string[];
  readonly requiredMeasures: readonly string[];
  readonly rationale: string;
  /** Confianza heuristica entre 0 y 1. */
  readonly confidence: number;
}

export interface SuggestVisualizationInput {
  readonly shape: QueryShape;
  readonly capabilities: readonly AIVisualizationCapability[];
  readonly dimensionKeys: readonly string[];
  readonly measureKeys: readonly string[];
  readonly current: VisualizationType | null;
}

/** Preferencias ordenadas por forma de consulta; la primera compatible y disponible gana. */
function preferences(
  shape: QueryShape,
): { type: VisualizationType; rationale: string; confidence: number }[] {
  const { dimensionCount: d, measureCount: m, temporalDimensionCount: t } = shape;
  const geographic = shape.geographicDimensionCount;
  if (m === 0) {
    return [
      {
        type: 'table',
        rationale: 'Sin medidas, una tabla lista los valores de las dimensiones.',
        confidence: 0.5,
      },
    ];
  }
  if (d === 0) {
    return [
      {
        type: 'kpi',
        rationale: 'Medidas sin dimensiones: un KPI destaca el valor total.',
        confidence: 0.9,
      },
      {
        type: 'table',
        rationale: 'Una tabla muestra los totales de las medidas.',
        confidence: 0.5,
      },
    ];
  }
  if (t > 0 && d <= 2) {
    return [
      {
        type: 'line',
        rationale: 'Hay una dimension temporal: una linea muestra la tendencia.',
        confidence: 0.9,
      },
      { type: 'column', rationale: 'Columnas comparan periodos consecutivos.', confidence: 0.7 },
      { type: 'table', rationale: 'Una tabla muestra el detalle por periodo.', confidence: 0.4 },
    ];
  }
  if (geographic > 0 && d <= 2 && m <= 2) {
    return [
      {
        type: 'map',
        rationale: 'Hay una dimension geografica: un mapa ubica los valores.',
        confidence: 0.75,
      },
      { type: 'bar', rationale: 'Barras comparan las regiones entre si.', confidence: 0.7 },
      { type: 'table', rationale: 'Una tabla muestra el detalle por region.', confidence: 0.4 },
    ];
  }
  if (d === 1 && m === 1) {
    return [
      {
        type: 'bar',
        rationale: 'Una dimension y una medida: barras comparan categorias.',
        confidence: 0.85,
      },
      { type: 'pie', rationale: 'Un circular muestra la composicion del total.', confidence: 0.5 },
      { type: 'table', rationale: 'Una tabla muestra el detalle.', confidence: 0.4 },
    ];
  }
  if (d <= 2) {
    return [
      {
        type: 'bar',
        rationale: 'Barras comparan varias medidas o series por categoria.',
        confidence: 0.75,
      },
      {
        type: 'grouped-table',
        rationale: 'Una tabla agrupada resume varias medidas.',
        confidence: 0.6,
      },
      { type: 'table', rationale: 'Una tabla muestra el detalle.', confidence: 0.4 },
    ];
  }
  return [
    {
      type: 'pivot',
      rationale: 'Tres o mas dimensiones se leen mejor en una tabla pivote.',
      confidence: 0.7,
    },
    { type: 'table', rationale: 'Una tabla muestra el detalle completo.', confidence: 0.5 },
  ];
}

const allows = (capability: AIVisualizationCapability, shape: QueryShape): boolean =>
  shape.dimensionCount >= capability.minDimensions &&
  (capability.maxDimensions === null || shape.dimensionCount <= capability.maxDimensions) &&
  shape.measureCount >= capability.minMeasures &&
  (capability.maxMeasures === null || shape.measureCount <= capability.maxMeasures) &&
  (!capability.requiresTemporal || shape.temporalDimensionCount > 0) &&
  (!capability.requiresGeographic || shape.geographicDimensionCount > 0);

/**
 * Sugiere la visualizacion mas adecuada para la forma de la consulta entre las
 * capacidades disponibles. Determinista; `null` si ninguna capacidad es compatible.
 *
 * @example
 * suggestVisualization({ shape, capabilities, dimensionKeys: ['region'], measureKeys: ['sales'], current: 'table' });
 * // { type: 'bar', confidence: 0.85, ... }
 */
export function suggestVisualization(
  input: SuggestVisualizationInput,
): VisualizationSuggestion | null {
  const byType = new Map(input.capabilities.map((capability) => [capability.type, capability]));
  for (const preference of preferences(input.shape)) {
    const capability = byType.get(preference.type);
    if (!capability || !allows(capability, input.shape)) continue;
    const already = input.current === preference.type;
    return {
      type: preference.type,
      label: capability.label,
      requiredDimensions: input.dimensionKeys.slice(0, capability.maxDimensions ?? undefined),
      requiredMeasures: input.measureKeys.slice(0, capability.maxMeasures ?? undefined),
      rationale: already
        ? `${preference.rationale} Ya es la visualizacion actual.`
        : preference.rationale,
      confidence: preference.confidence,
    };
  }
  return null;
}

/** Sugerencia a partir de un Analysis State validable contra el modelo. */
export function suggestVisualizationForState(
  state: AnalysisState,
  index: SemanticModelIndex,
  capabilities: readonly AIVisualizationCapability[],
): VisualizationSuggestion | null {
  return suggestVisualization({
    shape: describeQueryShape(state, index),
    capabilities,
    dimensionKeys: state.dimensions.map((d) => d.key),
    measureKeys: state.measures.map((m) => m.key),
    current: state.visualization.type,
  });
}

/** Forma de la consulta descrita solo con el contexto de IA (sin acceso al modelo). */
export function shapeFromContext(context: AIContext): QueryShape {
  const analysis = context.analysis;
  if (!analysis) {
    return {
      dimensionCount: 0,
      measureCount: 0,
      temporalDimensionCount: 0,
      geographicDimensionCount: 0,
    };
  }
  const fields = new Map(context.fields.map((field) => [field.id, field]));
  let temporal = 0;
  let geographic = 0;
  for (const dimension of analysis.dimensions) {
    const field = fields.get(dimension.dimensionId);
    if (dimension.timeGrain !== undefined || field?.temporal === true) temporal += 1;
    if (field?.geographic === true) geographic += 1;
  }
  return {
    dimensionCount: analysis.dimensions.length,
    measureCount: analysis.measures.length,
    temporalDimensionCount: temporal,
    geographicDimensionCount: geographic,
  };
}
